import { DisplayPageType } from "@faire/web-api/indigofair/data/DisplayPageType";
import { ILayoutElement } from "@faire/web-api/indigofair/data/ILayoutElement";
import { ProductTileContainer } from "@faire/web-api/indigofair/data/ProductTileContainer";
import { logWarning } from "@faire/web/common/logging";
import React, { PropsWithChildren } from "react";

import { ComponentLoggingEventParams } from "@faire/retailer/components/SDUI/__internal__/types";
import { ProductTileClickImpressionProvider } from "@faire/retailer/hooks/ProductTileImpression/ProductTileClickImpressionProvider";
import { ProductTileViewImpressionProvider } from "@faire/retailer/hooks/ProductTileImpression/ProductTileViewImpressionProvider";

interface IProductTileImpressionProviderProps {
  loggingData: ComponentLoggingEventParams;
}

export const SDUIProductTileImpressionProvider: React.FC<
  PropsWithChildren<IProductTileImpressionProviderProps>
> = ({ children, loggingData }) => {
  const productTileContainer = getProductTileContainer(loggingData);
  const context = getContext(productTileContainer, loggingData);
  const containerName = loggingData.component_name;
  const requestId = loggingData.request_id;

  return (
    <ProductTileViewImpressionProvider context={context} requestId={requestId}>
      <ProductTileClickImpressionProvider
        context={context}
        productTileContainer={productTileContainer}
        requestId={requestId}
        containerName={containerName}
      >
        {children}
      </ProductTileClickImpressionProvider>
    </ProductTileViewImpressionProvider>
  );
};

const getContext = (
  productTileContainer: ProductTileContainer,
  loggingData: ComponentLoggingEventParams
) => {
  if (!loggingData.component_name) {
    return `${productTileContainer}|unknown` as const;
  }
  if (
    !loggingData.component_instance_identifier &&
    !loggingData.view_instance_identifier
  ) {
    return `${productTileContainer}|${loggingData.component_name}` as const;
  }
  return `${productTileContainer}|${loggingData.component_name}|${
    loggingData.component_instance_identifier ??
    loggingData.view_instance_identifier
  }` as const;
};

const getProductTileContainer = (
  loggingData: ComponentLoggingEventParams
): ProductTileContainer => {
  switch (loggingData?.view_display_page) {
    case DisplayPageType.CATEGORY_PAGE:
      if (
        loggingData.section_type === "FilterablePaginatedSection" &&
        loggingData.component_type === ILayoutElement.Type.PRODUCT_TILE_GRID
      ) {
        return ProductTileContainer.CATEGORY_PRODUCT_RESULTS_GRID;
      }

      return ProductTileContainer.CATEGORY_PRODUCT_RESULTS_LAYOUT_ELEMENT_CAROUSEL;
    case DisplayPageType.HOMEPAGE:
      return ProductTileContainer.HOMEPAGE_LAYOUT_ELEMENT_CAROUSEL;
    default:
      logWarning(
        new Error(
          `SDUI ProductTileContainer is UNKNOWN for ${loggingData?.view_display_page}`
        ),
        {
          data: loggingData,
        }
      );
      return ProductTileContainer.PRODUCT_TILE_CONTAINER_UNKNOWN;
  }
};
